'use strict';

export const AUTH_SHELL_MIN_HEIGHT_VAR = '--auth-shell-min-height';
export const AUTH_SHELL_VIEWPORT_SHRINK_LOCK_PX = 24;

/**
 * Keep the last resting visual-viewport height when chrome shrinks
 * (software keyboard, iOS/Android autofill bar). Unlock on grow or
 * when width changes (orientation / split view).
 */
export function resolveAuthShellMinHeightPx({
  lockedPx,
  nextPx,
  nextWidthPx,
  lockedWidthPx,
  shrinkLockPx = AUTH_SHELL_VIEWPORT_SHRINK_LOCK_PX,
}) {
  if (!Number.isFinite(nextPx) || nextPx <= 0) {
    return Number.isFinite(lockedPx) && lockedPx > 0 ? lockedPx : 0;
  }
  if (
    Number.isFinite(nextWidthPx) &&
    Number.isFinite(lockedWidthPx) &&
    lockedWidthPx > 0 &&
    nextWidthPx !== lockedWidthPx
  ) {
    return nextPx;
  }
  if (!Number.isFinite(lockedPx) || lockedPx <= 0) {
    return nextPx;
  }
  if (nextPx < lockedPx - shrinkLockPx) {
    return lockedPx;
  }
  return nextPx;
}

export function readViewportSize(visualViewport, getViewportSize) {
  const fallback =
    typeof getViewportSize === 'function'
      ? getViewportSize()
      : { height: 0, width: 0 };
  const fallbackHeight = Number(fallback?.height);
  const fallbackWidth = Number(fallback?.width);

  if (visualViewport && Number.isFinite(visualViewport.height) && visualViewport.height > 0) {
    return {
      height: visualViewport.height,
      width:
        Number.isFinite(visualViewport.width) && visualViewport.width > 0
          ? visualViewport.width
          : fallbackWidth,
    };
  }

  return {
    height: Number.isFinite(fallbackHeight) ? fallbackHeight : 0,
    width: Number.isFinite(fallbackWidth) ? fallbackWidth : 0,
  };
}

/**
 * Lock auth shell min-height to the resting visual viewport so flex
 * `items-center` does not re-center the Sign In card when the keyboard
 * or autofill chrome shrinks the viewport.
 *
 * VisualViewport observer - see .cursor/rules/react-web-patterns.mdc.
 * Do not subscribe to visualViewport `scroll` (iOS A2HS jitter).
 */
export function createAuthShellViewportLock({
  getStage,
  visualViewport,
  getViewportSize,
  requestFrame = (cb) => setTimeout(cb, 0),
  addWindowListener,
  removeWindowListener,
}) {
  let lockedPx = 0;
  let lockedWidthPx = 0;
  let scheduled = false;

  const apply = () => {
    const stage = typeof getStage === 'function' ? getStage() : null;
    if (!stage || !stage.style) return;
    const size = readViewportSize(visualViewport, getViewportSize);
    const next = resolveAuthShellMinHeightPx({
      lockedPx,
      nextPx: size.height,
      nextWidthPx: size.width,
      lockedWidthPx,
    });
    if (!Number.isFinite(next) || next <= 0) return;
    lockedPx = next;
    if (Number.isFinite(size.width) && size.width > 0) {
      lockedWidthPx = size.width;
    }
    stage.style.setProperty(AUTH_SHELL_MIN_HEIGHT_VAR, `${next}px`);
  };

  const onResize = () => {
    if (scheduled) return;
    scheduled = true;
    requestFrame(() => {
      scheduled = false;
      apply();
    });
  };

  apply();

  if (visualViewport && typeof visualViewport.addEventListener === 'function') {
    visualViewport.addEventListener('resize', onResize);
  }
  if (typeof addWindowListener === 'function') {
    addWindowListener('resize', onResize);
    addWindowListener('orientationchange', onResize);
  }

  return {
    dispose() {
      if (visualViewport && typeof visualViewport.removeEventListener === 'function') {
        visualViewport.removeEventListener('resize', onResize);
      }
      if (typeof removeWindowListener === 'function') {
        removeWindowListener('resize', onResize);
        removeWindowListener('orientationchange', onResize);
      }
      const stage = typeof getStage === 'function' ? getStage() : null;
      if (stage && stage.style) {
        stage.style.removeProperty(AUTH_SHELL_MIN_HEIGHT_VAR);
      }
    },
    apply,
    getLockedPx() {
      return lockedPx;
    },
  };
}
